import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { managerActions } from '@store/reducers/manager/actions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import type React from 'react'
import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import API from '@/api'
import { ProjectPassword } from '../ProjectPassword'
import { AnonymousCommentsToggle } from './components/AnonymousCommentsToggle'
import { LinkPermissionSettings } from './components/LinkPermissionSettings'
import { Separator } from './components/Separator'
import { ShareSettings } from './components/ShareSettings'
import css from './ShareOptions.module.scss'
import { PROJECT_LINK_PERMISSION_TYPE, PROJECT_SHARE_TYPE } from './types'

const ShareOptions: React.FC = () => {
  const dispatch = useDispatch()

  const shareType = useSelector(migrationProjectSelector.getProjectShareType)

  const [selectedShareOption, setSelectedShareOption] =
    useState<PROJECT_SHARE_TYPE>(shareType)

  const hasPassword = shareType === PROJECT_SHARE_TYPE.PASSWORD

  const [showPassword, setShowPassword] = useState(hasPassword)

  const projectId = useSelector(migrationProjectSelector.getProjectId)
  const linkPermission = useSelector(
    migrationProjectSelector.getProjectLinkPermission
  )

  const changeShareType = async (shareType: PROJECT_SHARE_TYPE) => {
    try {
      const response = await API.Projects.shareType({
        projectId,
        shareType,
      })

      if (!response.ok) {
        dispatch(
          notificationActionCreators.setNotification({
            type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
          })
        )

        return
      }

      const data = await response.json()

      dispatch(managerActions.updateMeta(data))

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.ACCESS_SETTINGS_UPDATED,
        })
      )
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.log(error)

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
        })
      )
    }
  }

  const handleOnChangeSharePermissionOption = (value: string | number) => {
    const isPassword = value === PROJECT_SHARE_TYPE.PASSWORD
    setShowPassword(isPassword)
    setSelectedShareOption(value as PROJECT_SHARE_TYPE)

    if (!isPassword) {
      changeShareType(value as PROJECT_SHARE_TYPE)
    }
  }

  const showToggle =
    selectedShareOption !== PROJECT_SHARE_TYPE.ONLY_MEMBERS &&
    linkPermission !== PROJECT_LINK_PERMISSION_TYPE.VIEW

  return (
    <div className={css.container}>
      <div className={css.options}>
        <ShareSettings
          selected={selectedShareOption}
          onChange={handleOnChangeSharePermissionOption}
        />
        {selectedShareOption !== PROJECT_SHARE_TYPE.ONLY_MEMBERS && (
          <LinkPermissionSettings />
        )}
      </div>

      {showPassword && <ProjectPassword />}
      {!showPassword && <Separator className={css.separator} />}
      {showToggle && <AnonymousCommentsToggle share={selectedShareOption} />}
    </div>
  )
}

export default ShareOptions
